import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { TRIGGERS_BASE_QUERY_KEY } from 'components/domain/automation/triggers/triggersQuery';
import useDeleteTriggerMutation from 'components/domain/automation/triggers/useDeleteTriggerMutation';
import { modalIds } from 'components/modal/Modal.constants';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { Trigger } from 'models/automation/Triggers';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

const useDeleteTriggerModal = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { openConfirmModal, closeModal } = useAppModal();
  const { mutateAsync: deleteTrigger } = useDeleteTriggerMutation({
    onSuccess: () => {
      notifySuccess(t('pageAutomations.triggers.deleteModal.success'));
      closeModal(modalIds.confirm);
      queryClient.invalidateQueries({ queryKey: [TRIGGERS_BASE_QUERY_KEY] });
    },

    onError: (error) => {
      notifyError(
        getErrorMessage({
          apiErrorCode: error.response?.data?.apiErrorCode,
          apiErrorMessage:
            error.response?.data?.apiErrorMessage ??
            t('pageAutomations.triggers.deleteModal.error'),
          baseKey: 'pageAutomations.triggers.apiErrorCodes.',
          t,
        }),
      );
    },
  });

  const openDeleteTriggerModal = (trigger: Trigger) => {
    openConfirmModal({
      body: (
        <Trans
          i18nKey="pageAutomations.triggers.deleteModal.description"
          values={{ name: trigger.name }}
          components={{ br: <br /> }}
        />
      ),
      confirmColor: 'danger',
      confirmLabel: t('common.delete'),
      onConfirmAsync: async () => {
        await deleteTrigger({ triggerUid: trigger.uid });
      },
      title: t('pageAutomations.triggers.deleteModal.title'),
    });
  };

  return {
    openDeleteTriggerModal,
  };
};

export default useDeleteTriggerModal;
